<template>
  <Dialog
    v-model:visible="mostrarModal"
    :style="{ width: '600px' }"
    header="Firma del Plan de Tratamiento"
    :modal="true"
    :draggable="false"
    @hide="ocultarDialog"
    @show="inicializarCanvas"
  >
    <div class="p-fluid grid">
      <!-- Info del tratamiento -->
      <div class="field col-12">
        <div class="info-box">
          <span class="info-label">TRATAMIENTO:</span>
          <span class="info-value">{{ data.tratamiento }}</span>
        </div>
        <div class="info-box mt-1" v-if="data.dientes">
          <span class="info-label">PIEZA DENTAL:</span>
          <span class="info-value">{{ data.dientes }}</span>
        </div>
      </div>

      <!-- Si ya tiene firma -->
      <template v-if="data.firma">
        <div class="field col-12">
          <span class="firma-titulo">FIRMA REGISTRADA</span>
          <div class="firma-box firma-box-img">
            <img
              :src="
                data.firma.startsWith('data:')
                  ? data.firma
                  : 'data:image/png;base64,' + data.firma
              "
              alt="Firma"
              style="max-height: 130px; max-width: 100%"
            />
          </div>
          <div class="mt-2 text-center">
            <Button
              label="BORRAR FIRMA Y VOLVER A FIRMAR"
              icon="pi pi-refresh"
              class="p-button-sm p-button-warning"
              @click="borrarFirmaExistente"
            />
          </div>
        </div>
      </template>

      <!-- Canvas para nueva firma -->
      <template v-else>
        <div class="field col-12">
          <span class="firma-titulo">FIRMA PACIENTE / TUTOR / RESPONSABLE</span>
          <div class="firma-box">
            <canvas ref="canvasFirma" class="canvas-firma"></canvas>
            <Button
              icon="pi pi-refresh"
              class="p-button-sm p-button-warning p-button-text firma-btn-limpiar"
              v-tooltip.top="'Limpiar firma'"
              @click="limpiarFirma"
            />
          </div>
          <small class="text-500"
            >Firme en el recuadro con el mouse o dedo</small
          >
        </div>
      </template>
    </div>

    <template #footer>
      <Button
        label="CANCELAR"
        icon="pi pi-times"
        class="p-button-secondary p-button-lg"
        @click="ocultarDialog"
      />
      <Button
        v-if="!data.firma"
        label="GUARDAR FIRMA"
        icon="pi pi-save"
        class="p-button-info p-button-lg"
        :loading="guardando"
        @click="guardarFirma"
      />
    </template>
  </Dialog>
</template>

<script>
import SignaturePad from "signature_pad";
import PlanTratamientoService from "@/service/PlanTratamientoService";
import { nextTick } from "vue";

export default {
  name: "FirmaPlanTratamiento",
  emits: ["closeModal", "firmaGuardada"],
  props: {
    show: { type: Boolean, default: false },
    planTratamiento: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      mostrarModal: this.show,
      data: { ...this.planTratamiento },
      signaturePad: null,
      guardando: false,
    };
  },
  planTratamientoService: null,
  created() {
    this.planTratamientoService = new PlanTratamientoService();
  },
  methods: {
    inicializarCanvas() {
      if (this.data.firma) return; // Ya tiene firma, no inicializar canvas
      nextTick(() => {
        setTimeout(() => {
          const canvas = this.$refs.canvasFirma;
          if (!canvas) return;
          canvas.width = canvas.offsetWidth || 500;
          canvas.height = 130;
          if (this.signaturePad) {
            this.signaturePad.off();
          }
          this.signaturePad = new SignaturePad(canvas, {
            backgroundColor: "rgb(255, 255, 255)",
          });
        }, 100);
      });
    },
    limpiarFirma() {
      if (this.signaturePad) this.signaturePad.clear();
    },
    borrarFirmaExistente() {
      this.data = { ...this.data, firma: null };
      nextTick(() => this.inicializarCanvas());
    },
    guardarFirma() {
      if (!this.signaturePad || this.signaturePad.isEmpty()) {
        this.$toast.add({
          severity: "warn",
          summary: "Firma requerida",
          detail: "Por favor firme en el recuadro antes de guardar",
          life: 3000,
        });
        return;
      }

      this.guardando = true;
      const firma = this.signaturePad.toDataURL("image/png");

      this.planTratamientoService
        .guardarFirmaPlan({
          plan_tratamiento_id: this.data.id,
          firma,
        })
        .then((response) => {
          if (!response.success) {
            this.$toast.add({
              severity: "error",
              summary: "Error",
              detail: response.message,
              life: 3000,
            });
            return;
          }
          this.$toast.add({
            severity: "success",
            summary: "Éxito",
            detail: "Firma guardada correctamente",
            life: 2500,
          });
          this.$emit("firmaGuardada", {
            id: this.data.id,
            firma: response.firma,
          });
          this.ocultarDialog();
        })
        .catch(() => {
          this.$toast.add({
            severity: "error",
            summary: "Error",
            detail: "No se pudo guardar la firma",
            life: 3000,
          });
        })
        .finally(() => {
          this.guardando = false;
        });
    },
    ocultarDialog() {
      if (this.signaturePad) {
        this.signaturePad.off();
        this.signaturePad = null;
      }
      this.mostrarModal = false;
      this.$emit("closeModal");
    },
  },
  watch: {
    show(val) {
      this.mostrarModal = val;
      if (val) {
        this.data = { ...this.planTratamiento };
      }
    },
    planTratamiento(val) {
      this.data = { ...val };
    },
  },
};
</script>

<style scoped lang="scss">
.info-box {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 8px 12px;
  display: flex;
  gap: 8px;
  align-items: center;
}
.info-label {
  font-weight: bold;
  font-size: 12px;
  color: #002236;
  white-space: nowrap;
}
.info-value {
  font-size: 13px;
  color: #333;
}
.firma-titulo {
  font-size: 13px;
  font-weight: bold;
  text-transform: uppercase;
  text-align: center;
  display: block;
  margin-bottom: 8px;
  color: #002236;
  background: linear-gradient(135deg, #f0f0f0 0%, #e0e0e0 100%);
  padding: 8px;
  border-radius: 5px;
}
.firma-box {
  border: 2px dashed #002236;
  border-radius: 6px;
  padding: 6px;
  position: relative;
  background: #fff;
  min-height: 130px;
}
.firma-box-img {
  display: flex;
  align-items: center;
  justify-content: center;
  border-style: solid;
}
.canvas-firma {
  width: 100% !important;
  height: 130px;
  display: block;
  border-radius: 4px;
  cursor: crosshair;
}
.firma-btn-limpiar {
  position: absolute;
  bottom: 4px;
  right: 4px;
  opacity: 0.7;
}
</style>